import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SparklesIcon, CheckBadgeIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

const CertificateNotification = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // تغییر key برای نمایش دوباره نوتیفیکیشن
    const dismissed = sessionStorage.getItem('novayar_notification_v2_dismissed');
    
    if (!dismissed) {
      const timer = setTimeout(() => setIsVisible(true), 2500);
      const autoHideTimer = setTimeout(() => handleClose(), 20000);

      return () => {
        clearTimeout(timer);
        clearTimeout(autoHideTimer);
      };
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('novayar_notification_dismissed', 'true');
  };

  const handleClick = () => {
    // هدایت به صفحه دانلود اپلیکیشن
    navigate('/download-app');
    handleClose();
  };

  if (isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: -400, opacity: 0, scale: 0.8 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: -400, opacity: 0, scale: 0.8 }}
          transition={{ 
            type: 'spring', 
            stiffness: 200, 
            damping: 20
          }}
          className="fixed top-5 left-5 right-5 sm:right-auto sm:max-w-md md:max-w-lg z-50"
          data-testid="certificate-notification"
        >
          {/* Premium Card */}
          <motion.div
            onClick={handleClick}
            whileHover={{ scale: 1.02, y: -3, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)' }}
            whileTap={{ scale: 0.98 }}
            className="relative rounded-3xl shadow-2xl overflow-hidden backdrop-blur-2xl bg-gradient-to-br from-white via-blue-50/80 to-purple-50/70 dark:from-gray-800 dark:via-gray-900 dark:to-blue-950/80 border-2 border-white/40 dark:border-blue-400/30 cursor-pointer transition-all duration-300"
          >
            {/* Dynamic Animated Background Orbs */}
            <div className="absolute inset-0 opacity-30 dark:opacity-20">
              <motion.div 
                className="absolute -top-12 -right-12 w-48 h-48 bg-gradient-to-br from-blue-400 via-purple-500 to-indigo-500 rounded-full blur-3xl"
                animate={{ 
                  scale: [1, 1.3, 1],
                  rotate: [0, 120, 0],
                  opacity: [0.4, 0.7, 0.4] 
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div 
                className="absolute -bottom-12 -left-12 w-48 h-48 bg-gradient-to-tr from-cyan-400 via-blue-500 to-purple-500 rounded-full blur-3xl"
                animate={{ 
                  scale: [1, 1.4, 1],
                  rotate: [0, -120, 0],
                  opacity: [0.4, 0.8, 0.4] 
                }}
                transition={{ duration: 4.5, repeat: Infinity, delay: 0.5, ease: "easeInOut" }}
              />
            </div>

            {/* Main Content */}
            <div className="relative p-5 md:p-6">
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.15, rotate: 90 }}
                whileTap={{ scale: 0.85 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="absolute top-3 left-3 p-2 rounded-full bg-white/90 dark:bg-gray-700/90 hover:bg-red-50 dark:hover:bg-red-900/50 text-gray-400 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 shadow-lg transition-all duration-200 z-10 backdrop-blur-sm"
                data-testid="notification-close-btn"
              >
                <XMarkIcon className="w-5 h-5" />
              </motion.button>

              {/* Top Section: Icon + Title */}
              <div className="flex items-start gap-4 mb-4">
                {/* Large Animated Icon */}
                <motion.div
                  animate={{ 
                    y: [0, -8, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    y: { duration: 2.5, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                  }}
                  className="relative flex-shrink-0"
                >
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl blur-xl opacity-70 animate-pulse"></div>
                  
                  {/* Icon Container - Robot for Novayar */}
                  <div className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 p-4 md:p-5 rounded-2xl shadow-2xl">
                    <span className="text-4xl md:text-5xl">🤖</span>
                  </div>
                  
                  {/* Success Badge */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                    className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5 shadow-xl ring-4 ring-white dark:ring-gray-800"
                  >
                    <CheckBadgeIcon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </motion.div>
                </motion.div>

                {/* Text Content */}
                <div className="flex-1 pt-1 pr-8">
                  <motion.h3 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-base md:text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 leading-tight mb-1.5"
                  >
                    نوآیار اومده! 🤖
                  </motion.h3>
                  
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-sm md:text-base text-gray-700 dark:text-gray-200 leading-relaxed font-medium"
                  >
                    دستیار هوشمندی که مخصوص <span className="font-bold text-blue-600 dark:text-blue-400">طرح</span> و <span className="font-bold text-purple-600 dark:text-purple-400">کسب‌وکار</span> تو ساخته شده.
                  </motion.p>
                </div>
              </div>

              {/* Description Box */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-4 bg-gradient-to-r from-blue-50/80 to-purple-50/80 dark:from-gray-700/50 dark:to-purple-900/30 rounded-2xl p-4 border border-blue-200/40 dark:border-purple-500/30 backdrop-blur-sm"
              >
                <p className="text-sm md:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                  برای استفاده، فقط <span className="font-bold text-green-600 dark:text-green-400">اپلیکیشن رو نصب کن</span>
                  <motion.span
                    animate={{ x: [-2, 2, -2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="inline-block mr-1"
                  >
                    📱
                  </motion.span>
                </p>
              </motion.div>

              {/* Progress Bar */}
              <div className="h-2 bg-gray-200/70 dark:bg-gray-700/70 rounded-full overflow-hidden shadow-inner">
                <motion.div 
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full shadow-lg"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: 20, ease: 'linear' }}
                />
              </div>
            </div>

            {/* Premium Shine Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-white/20 to-transparent pointer-events-none"
              initial={{ x: '-100%' }}
              animate={{ x: '200%' }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                repeatDelay: 2,
                ease: 'easeInOut'
              }}
            />

            {/* Decorative Sparkles */}
            <motion.div
              className="absolute top-6 right-6"
              animate={{ 
                scale: [1, 1.4, 1],
                rotate: [0, 180, 360],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <SparklesIcon className="w-5 h-5 text-blue-400 drop-shadow-lg" />
            </motion.div>
            
            <motion.div
              className="absolute bottom-20 left-6"
              animate={{ 
                scale: [1, 1.3, 1],
                rotate: [360, 180, 0],
                opacity: [0.4, 0.9, 0.4]
              }}
              transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
            >
              <SparklesIcon className="w-4 h-4 text-purple-400 drop-shadow-lg" />
            </motion.div>

            <motion.div
              className="absolute top-1/2 right-8"
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 90, 180],
                opacity: [0.3, 0.7, 0.3]
              }}
              transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
            >
              <SparklesIcon className="w-3 h-3 text-indigo-400 drop-shadow-lg" />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CertificateNotification;
